// =============================================================================
// MeuDriver — Admin: disputas e cobranças contestáveis
//
// Modelo de carteira (2026-10): a aula é paga com saldo travado. Cancelamento
// com antecedência devolve sozinho (trigger bookings_settle_credit), então NÃO
// é disputa. O que pede olho do admin:
//   1. disputas abertas pelo aluno (tabela `disputes`);
//   2. aulas que consumiram o crédito sem a aula acontecer (ausência e
//      cancelamento tardio) — o aluno pode reclamar por fora;
//   3. histórico das resolvidas.
// "Devolver ao saldo" chama admin-refund → wallet_refund_booking (devolve o
// valor travado e reverte o repasse ainda não pago).
// Antes (até 2026-10-12) a página listava todo cancelamento e "resolvia"
// marcando `payments` como reembolsado — sem mover dinheiro nenhum.
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { CheckCircle2, RotateCcw, XCircle } from 'lucide-react'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface BookingInfo {
  id: string
  status: string
  scheduled_at: string
  total_price: number | null
  wallet_amount: number | null
  settlement: string | null
  student_package_id: string | null
  student: { full_name: string | null } | null
  instructor: { profile: { full_name: string | null } | null } | null
}

interface DisputeRow {
  id: string
  reason: string
  description: string
  status: string
  resolution_note: string | null
  created_at: string
  resolved_at: string | null
  booking: BookingInfo | null
}

type Action =
  | { kind: 'refund'; booking: BookingInfo; disputeId?: string }
  | { kind: 'close'; dispute: DisputeRow }

const BOOKING_FIELDS = `id, status, scheduled_at, total_price, wallet_amount, settlement, student_package_id,
  student:profiles!bookings_student_id_fkey(full_name),
  instructor:instructors!bookings_instructor_id_fkey(profile:profiles!instructors_profile_id_fkey(full_name))`

// ---------------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------------

const REASON_LABEL: Record<string, string> = {
  no_show_instructor: 'Instrutor não compareceu',
  no_show_student: 'Aluno não compareceu',
  quality_issue: 'Qualidade da aula',
  wrong_amount: 'Valor cobrado incorreto',
  unsafe_behavior: 'Comportamento inadequado',
  other: 'Outro motivo',
}

const STATUS_LABEL: Record<string, string> = {
  completed: 'Concluída',
  no_show: 'Ausência do aluno',
  cancelled_by_student: 'Cancelada pelo aluno',
  cancelled_by_instructor: 'Cancelada pelo instrutor',
  confirmed: 'Confirmada',
  in_progress: 'Em andamento',
  pending_payment: 'Aguardando pagamento',
}

const SETTLEMENT_LABEL: Record<string, string> = {
  consumed: 'crédito consumido',
  released: 'valor devolvido',
  refunded: 'devolvido pelo suporte',
}

const REFUND_ERRORS: Record<string, string> = {
  earning_already_paid: 'O repasse desta aula já foi pago ao instrutor — devolva por fora e registre na nota.',
  package_lesson_refund_not_supported: 'Aula de pacote: a devolução automática não cobre pacotes.',
  booking_not_found: 'Aula não encontrada.',
}

/** Só dá para devolver o que ainda está consumido e veio do saldo. */
function canRefund(b: BookingInfo | null): b is BookingInfo {
  return !!b && b.settlement === 'consumed' && Number(b.wallet_amount ?? 0) > 0
}

// ---------------------------------------------------------------------------
// Peças de tela
// ---------------------------------------------------------------------------

function BookingLine({ b }: { b: BookingInfo }) {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-0.5 text-sm sm:grid-cols-2 lg:grid-cols-4">
      <div><span className="text-muted-foreground">Aluno: </span><span className="font-medium">{b.student?.full_name ?? '—'}</span></div>
      <div><span className="text-muted-foreground">Instrutor: </span><span className="font-medium">{b.instructor?.profile?.full_name ?? '—'}</span></div>
      <div><span className="text-muted-foreground">Aula: </span>{formatDate(b.scheduled_at, "dd/MM/yyyy 'às' HH:mm")}</div>
      <div>
        <span className="text-muted-foreground">Valor: </span>
        <span className="font-semibold">{formatCurrency(b.total_price ?? 0)}</span>
        {b.settlement && <span className="text-xs text-muted-foreground"> · {SETTLEMENT_LABEL[b.settlement] ?? b.settlement}</span>}
      </div>
    </div>
  )
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
      <CheckCircle2 className="mb-2 h-8 w-8 text-green-500" />
      <p className="text-sm">{text}</p>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-md" />)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function AdminDisputes() {
  const [open, setOpen] = useState<DisputeRow[]>([])
  const [charges, setCharges] = useState<BookingInfo[]>([])
  const [resolved, setResolved] = useState<DisputeRow[]>([])
  const [loading, setLoading] = useState(true)

  const [action, setAction] = useState<Action | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString()
      const [openRes, resolvedRes, chargesRes] = await Promise.all([
        supabase
          .from('disputes')
          .select(`id, reason, description, status, resolution_note, created_at, resolved_at, booking:bookings!disputes_booking_id_fkey(${BOOKING_FIELDS})`)
          .in('status', ['open', 'under_review'])
          .order('created_at', { ascending: true }),
        supabase
          .from('disputes')
          .select(`id, reason, description, status, resolution_note, created_at, resolved_at, booking:bookings!disputes_booking_id_fkey(${BOOKING_FIELDS})`)
          .in('status', ['resolved', 'closed'])
          .order('resolved_at', { ascending: false })
          .limit(10),
        supabase
          .from('bookings')
          .select(BOOKING_FIELDS)
          .in('status', ['no_show', 'cancelled_by_student'])
          .eq('settlement', 'consumed')
          .gte('scheduled_at', since)
          .order('scheduled_at', { ascending: false }),
      ])
      if (openRes.error) throw openRes.error
      if (resolvedRes.error) throw resolvedRes.error
      if (chargesRes.error) throw chargesRes.error

      const openRows = (openRes.data ?? []) as unknown as DisputeRow[]
      const disputed = new Set(openRows.map((d) => d.booking?.id))
      setOpen(openRows)
      setResolved((resolvedRes.data ?? []) as unknown as DisputeRow[])
      setCharges(((chargesRes.data ?? []) as unknown as BookingInfo[]).filter((b) => !disputed.has(b.id)))
    } catch {
      toast.error('Erro ao carregar disputas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  function start(a: Action) {
    setNote('')
    setAction(a)
  }

  async function confirm() {
    if (!action) return
    const trimmed = note.trim()
    if (action.kind === 'close' && trimmed.length < 5) {
      toast.error('Escreva o motivo da decisão (aparece no histórico).')
      return
    }
    setBusy(true)
    try {
      if (action.kind === 'refund') {
        const { data, error } = await supabase.functions.invoke('admin-refund', {
          body: { booking_id: action.booking.id, reason: trimmed || undefined },
        })
        if (error) {
          let code = ''
          try { code = (await (error as { context?: Response }).context?.json())?.error ?? '' } catch { /* corpo ilegível */ }
          throw new Error(REFUND_ERRORS[code] ?? 'Não foi possível devolver o valor.')
        }
        const amount = Number((data as { refunded_to_wallet?: number })?.refunded_to_wallet ?? 0)
        if (action.disputeId) {
          await supabase
            .from('disputes')
            .update({
              status: 'resolved',
              resolution_note: `Devolvido ao saldo do aluno (${formatCurrency(amount)})${trimmed ? ` — ${trimmed}` : ''}`,
              resolved_at: new Date().toISOString(),
            })
            .eq('id', action.disputeId)
        }
        toast.success(amount > 0 ? `${formatCurrency(amount)} devolvidos ao saldo do aluno.` : 'Nada a devolver: o valor já tinha voltado ao saldo.')
      } else {
        const { error } = await supabase
          .from('disputes')
          .update({ status: 'resolved', resolution_note: `Sem devolução — ${trimmed}`, resolved_at: new Date().toISOString() })
          .eq('id', action.dispute.id)
        if (error) throw new Error('Não foi possível encerrar a disputa.')
        toast.success('Disputa encerrada sem devolução.')
      }
      setAction(null)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado')
    } finally {
      setBusy(false)
    }
  }

  const actionBooking = action?.kind === 'refund' ? action.booking : action?.dispute.booking ?? null

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Disputas</h1>
        <p className="mt-1 text-muted-foreground">
          Reclamações dos alunos e aulas cobradas sem acontecer. Cancelamentos com antecedência
          devolvem o valor sozinhos e não aparecem aqui.
        </p>
      </div>

      {/* 1. Disputas abertas */}
      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle className="flex items-center gap-2">
            Disputas abertas
            {!loading && open.length > 0 && <Badge variant="destructive">{open.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <ListSkeleton /> : open.length === 0 ? (
            <EmptyState text="Nenhuma disputa aberta." />
          ) : (
            <div className="divide-y">
              {open.map((d) => (
                <div key={d.id} className="space-y-3 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-red-200 bg-red-50 text-red-800">{REASON_LABEL[d.reason] ?? d.reason}</Badge>
                    <span className="text-xs text-muted-foreground">aberta em {formatDate(d.created_at, "dd/MM/yyyy 'às' HH:mm")}</span>
                    {d.booking && <span className="text-xs text-muted-foreground">· aula {STATUS_LABEL[d.booking.status] ?? d.booking.status}</span>}
                  </div>
                  <p className="rounded-md bg-muted/50 p-3 text-sm">{d.description}</p>
                  {d.booking && <BookingLine b={d.booking} />}
                  <div className="flex flex-wrap gap-2">
                    {canRefund(d.booking) && (
                      <Button size="sm" variant="outline" className="gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClick={() => start({ kind: 'refund', booking: d.booking!, disputeId: d.id })}>
                        <RotateCcw className="h-3.5 w-3.5" /> Devolver ao saldo
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => start({ kind: 'close', dispute: d })}>
                      <XCircle className="h-3.5 w-3.5" /> Encerrar sem devolução
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Cobranças sem aula */}
      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle>Cobradas sem a aula acontecer (30 dias)</CardTitle>
          <p className="text-sm text-muted-foreground">
            Ausência do aluno e cancelamento em cima da hora consomem o crédito. Confira se algum
            caso merece devolução.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <ListSkeleton /> : charges.length === 0 ? (
            <EmptyState text="Nenhuma aula cobrada por ausência ou cancelamento tardio." />
          ) : (
            <div className="divide-y">
              {charges.map((b) => (
                <div key={b.id} className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1 space-y-1">
                    <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-800">
                      {b.status === 'no_show' ? 'Ausência do aluno' : 'Cancelamento tardio'}
                    </Badge>
                    <BookingLine b={b} />
                  </div>
                  {canRefund(b) && (
                    <Button size="sm" variant="outline" className="shrink-0 gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => start({ kind: 'refund', booking: b })}>
                      <RotateCcw className="h-3.5 w-3.5" /> Devolver ao saldo
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. Histórico */}
      {!loading && resolved.length > 0 && (
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle>Resolvidas recentemente</CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {resolved.map((d) => (
              <div key={d.id} className="space-y-1 p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{REASON_LABEL[d.reason] ?? d.reason}</Badge>
                  {d.resolved_at && <span className="text-xs text-muted-foreground">resolvida em {formatDate(d.resolved_at, 'dd/MM/yyyy')}</span>}
                  <span className="text-xs text-muted-foreground">
                    {d.booking?.student?.full_name ?? '—'} × {d.booking?.instructor?.profile?.full_name ?? '—'}
                  </span>
                </div>
                {d.resolution_note && <p className="text-muted-foreground">{d.resolution_note}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Confirmação */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o && !busy) setAction(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === 'refund' ? 'Devolver ao saldo do aluno' : 'Encerrar sem devolução'}
            </DialogTitle>
            <DialogDescription>
              {action?.kind === 'refund' && actionBooking ? (
                <>
                  {formatCurrency(Number(actionBooking.wallet_amount ?? 0))} voltam para o saldo de{' '}
                  <strong>{actionBooking.student?.full_name ?? 'aluno'}</strong> e o repasse ainda não pago a{' '}
                  <strong>{actionBooking.instructor?.profile?.full_name ?? 'instrutor'}</strong> é cancelado.
                </>
              ) : (
                <>A cobrança fica como está e a disputa é encerrada. O motivo fica no histórico.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="dispute-note">{action?.kind === 'close' ? 'Motivo (obrigatório)' : 'Observação (opcional)'}</Label>
            <Textarea id="dispute-note" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={action?.kind === 'close' ? 'Ex.: aluno faltou sem avisar, conforme combinado.' : 'Ex.: instrutor confirmou que não compareceu.'} />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setAction(null)}>Voltar</Button>
            <Button disabled={busy} onClick={confirm}
              className={action?.kind === 'refund' ? 'bg-orange-600 hover:bg-orange-700' : undefined}>
              {busy ? 'Processando…' : action?.kind === 'refund' ? 'Devolver' : 'Encerrar disputa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
